import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Meteor } from 'meteor/meteor';
import { restaurantMatchesSearch } from '../../lib/restaurantSearch';
import {
  filterForVenues,
  isOrgFilter,
  ORG_FILTER,
  orgFilterLabel,
  outletFilterKey,
  restaurantIdsForScopeFilter,
  restaurantIdSetsEqual,
  scopeGroupFilterKey,
  scopeGroupIdFromFilter,
  VENUES_FILTER_MAX,
} from '../../lib/scopeFilter';
import type { FleetRestaurant, ScopeGroupConfig, ScopeGroupSummary } from '../../types';
import { Icon } from '../shared/Icon';
import { onListKeyDown } from './overlay';

export const RESULT_CAP = 200;
export const MAX_COMPARE = 5;
/** Venues named one by one in the Selected row before "+N more". */
export const TOKEN_CAP = 12;

/** Muted, dashboard-grade hues for venues compared side by side. */
export const COMPARE_COLORS = ['#1f7a8c', '#a35d6a', '#c8963e', '#5e8c61', '#7a6c9e'] as const;

export type VenueCompareDraft = {
  /** The screen can compare venues side by side (Performance, Live, Settings). */
  supported: boolean;
  on: boolean;
  filters: string[];
};

/**
 * The venues of the Filters panel (decisions 7.7.1, 7.7.2): what is selected
 * as removable tokens, "All venues of <group>" and the person's own groups as
 * checkboxes that add or remove their venues (partly ticked when only some
 * are in), and every venue they can open as a checklist with search. A
 * selection that is exactly one group stays that group; anything else is a
 * set of venues. The draft is the venue filter; nothing applies until the
 * panel's Apply.
 */
export function VenueSection({
  draft,
  onDraft,
  restaurants,
  groups,
  groupConfigs,
  orgVenues,
  activeRestaurantId,
  restaurantId,
  onGroupsChanged,
  onManageGroups,
  compare,
  onCompare,
  screenName,
}: {
  draft: string | null;
  onDraft: (filter: string | null) => void;
  restaurants: FleetRestaurant[];
  groups: ScopeGroupSummary[];
  groupConfigs: ScopeGroupConfig[];
  orgVenues: { name: string | null; restaurantIds: string[] } | null;
  activeRestaurantId: string;
  /** The restaurant the groups are saved under. */
  restaurantId: string;
  onGroupsChanged?: () => void | Promise<void>;
  onManageGroups?: () => void;
  compare: VenueCompareDraft;
  onCompare: (next: VenueCompareDraft) => void;
  screenName: string;
}) {
  const { t } = useTranslation('chrome');
  const [search, setSearch] = useState('');
  const [selectedOnly, setSelectedOnly] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveNote, setSaveNote] = useState<{ ok: boolean; text: string } | null>(null);
  const [limitNote, setLimitNote] = useState(false);
  const orgIds = orgVenues?.restaurantIds ?? [];
  const showOrg = orgIds.length > 1;

  const idsOf = (filter: string | null) =>
    filter === null
      ? []
      : restaurantIdsForScopeFilter(filter, groupConfigs, activeRestaurantId, orgVenues?.restaurantIds ?? null);
  const ids = useMemo(() => idsOf(draft), [draft, groupConfigs, activeRestaurantId, orgVenues]);
  const checked = useMemo(() => new Set(ids), [ids]);
  // Rows ticked when the panel opened come first, so a selection isn't lost in a long list.
  const [pinned] = useState(() => new Set(ids));
  const compareSet = useMemo(() => new Set(compare.filters), [compare.filters]);

  const rows = useMemo(() => {
    const base = restaurants.filter(r => restaurantMatchesSearch(r, search));
    const shown = selectedOnly ? base.filter(r => checked.has(r.id)) : base;
    return [...shown].sort((a, b) => {
      const aa = a.id === activeRestaurantId ? 0 : 1;
      const bb = b.id === activeRestaurantId ? 0 : 1;
      if (aa !== bb) return aa - bb;
      const ap = pinned.has(a.id) ? 0 : 1;
      const bp = pinned.has(b.id) ? 0 : 1;
      if (ap !== bp) return ap - bp;
      return a.name.localeCompare(b.name);
    });
  }, [restaurants, search, selectedOnly, checked, activeRestaurantId, pinned]);
  const visible = rows.slice(0, RESULT_CAP);

  // "All venues of" and the person's groups, as the filters that name them.
  const namedFilters = [...(showOrg ? [ORG_FILTER] : []), ...groups.map(g => scopeGroupFilterKey(g.id))];

  /**
   * The venues as the draft. With `named`, a selection that is exactly "All
   * venues of" or one group becomes that filter (`prefer` first), so it keeps
   * its name and follows the group's edits.
   */
  const setIds = (next: string[], named = false, prefer?: string) => {
    const unique = [...new Set(next)];
    if (!unique.length) {
      setLimitNote(false);
      onDraft(null);
      return;
    }
    // The opened restaurant alone is the default, not a pick.
    if (unique.length === 1 && unique[0] === activeRestaurantId) {
      setLimitNote(false);
      onDraft('all');
      return;
    }
    if (named && unique.length > 1) {
      const set = new Set(unique);
      const match = [...(prefer ? [prefer] : []), ...namedFilters].find(f => {
        const members = idsOf(f);
        return members.length === set.size && restaurantIdSetsEqual(new Set(members), set);
      });
      if (match) {
        setLimitNote(false);
        onDraft(match);
        return;
      }
    }
    // A set of venues travels in the URL, so it is capped; a group is not.
    if (unique.length > VENUES_FILTER_MAX) {
      setLimitNote(true);
      return;
    }
    setLimitNote(false);
    onDraft(filterForVenues(unique));
  };

  /**
   * How much of a group is selected: all, some or none of its venues. The
   * default is no pick, so it leaves every group unticked.
   */
  const groupState = (filter: string): { members: string[]; picked: number } => {
    const members = idsOf(filter);
    if (!members.length) return { members, picked: draft === filter ? 1 : 0 };
    if (draft === 'all') return { members, picked: 0 };
    return { members, picked: members.filter(id => checked.has(id)).length };
  };

  /**
   * A group's checkbox adds its venues to the selection, or takes them out
   * when all are in. From the default (the opened venue alone, not a pick)
   * it replaces the selection.
   */
  const toggleGroup = (filter: string) => {
    const members = idsOf(filter);
    if (!members.length) {
      onDraft(filter);
      return;
    }
    if (members.every(id => checked.has(id)) && draft !== 'all') {
      const out = new Set(members);
      setIds(
        ids.filter(id => !out.has(id)),
        true,
      );
      return;
    }
    setIds([...(draft === 'all' ? [] : ids), ...members], true, filter);
  };

  const toggle = (id: string) => {
    if (compare.on) {
      toggleCompare(outletFilterKey(id));
      return;
    }
    setIds(checked.has(id) ? ids.filter(x => x !== id) : [...ids, id]);
  };

  const toggleCompare = (filter: string) => {
    if (compareSet.has(filter)) {
      onCompare({ ...compare, filters: compare.filters.filter(f => f !== filter) });
    } else if (compare.filters.length < MAX_COMPARE) {
      onCompare({ ...compare, filters: [...compare.filters, filter] });
    }
  };

  // Select and clear act on every match, not only the rows drawn.
  const allMatchesChecked = rows.length > 1 && rows.every(r => checked.has(r.id));
  const selectAllSize = useMemo(() => new Set([...ids, ...rows.map(r => r.id)]).size, [ids, rows]);
  const toggleAll = () => {
    if (allMatchesChecked) {
      const out = new Set(rows.map(r => r.id));
      setIds(ids.filter(id => !out.has(id)));
    } else setIds([...ids, ...rows.map(r => r.id)]);
  };

  /**
   * The selection as tokens: "All venues of" and the person's groups that are
   * at least half in it (whole groups first, then the most picked, with
   * "226/227" when not whole), then the venues left, one by one.
   */
  const tokens = useMemo(() => {
    const out: { key: string; label: string; picked: number; total: number; remove: string[] }[] = [];
    const covered = new Set<string>();
    const candidates = namedFilters
      .map(f => {
        const members = idsOf(f);
        return { f, members, picked: members.filter(id => checked.has(id)) };
      })
      .filter(c => c.picked.length > 1 && c.picked.length * 2 >= c.members.length)
      .sort(
        (a, b) =>
          Number(b.picked.length === b.members.length) - Number(a.picked.length === a.members.length) ||
          b.picked.length - a.picked.length,
      );
    for (const c of candidates) {
      if (c.picked.every(id => covered.has(id))) continue;
      c.picked.forEach(id => covered.add(id));
      const groupId = scopeGroupIdFromFilter(c.f);
      out.push({
        key: c.f,
        label: groupId ? (groups.find(g => g.id === groupId)?.name ?? '') : orgFilterLabel(orgVenues?.name ?? null),
        picked: c.picked.length,
        total: c.members.length,
        remove: c.picked,
      });
    }
    const byId = new Map(restaurants.map(r => [r.id, r.name]));
    for (const id of ids) {
      if (covered.has(id)) continue;
      out.push({ key: id, label: byId.get(id) ?? id, picked: 1, total: 1, remove: [id] });
    }
    return out;
  }, [ids, checked, groups, groupConfigs, orgVenues, restaurants, showOrg]);
  const groupTokens = tokens.filter(tk => tk.total > 1);
  const venueTokens = tokens.filter(tk => tk.total === 1);
  const shownTokens = [...groupTokens, ...venueTokens.slice(0, TOKEN_CAP)];
  const moreTokens = venueTokens.length - Math.min(venueTokens.length, TOKEN_CAP);
  const removeToken = (remove: string[]) => {
    const out = new Set(remove);
    setIds(
      ids.filter(id => !out.has(id)),
      true,
    );
  };

  const saveGroup = async () => {
    const name = saveName.trim();
    if (!name || !ids.length) {
      setSaveNote({ ok: false, text: t('context.panel.venues.enterName') });
      return;
    }
    setSaving(true);
    setSaveNote(null);
    try {
      const savedId = await Meteor.callPromise<string>('opsCommand/saveScopeGroup', restaurantId, {
        name,
        restaurantIds: ids,
      });
      await onGroupsChanged?.();
      onDraft(scopeGroupFilterKey(savedId));
      setSaveOpen(false);
      setSaveName('');
      setSaveNote({ ok: true, text: t('context.panel.venues.saved', { name }) });
    } catch (e) {
      const err = e as { reason?: string; message?: string };
      setSaveNote({ ok: false, text: err.reason || err.message || t('context.panel.venues.saveFailed') });
    } finally {
      setSaving(false);
    }
  };

  const groupRow = (filter: string, label: string, sub: string, key: string) => {
    if (compare.on) {
      const on = compareSet.has(filter);
      const disabled = !on && compare.filters.length >= MAX_COMPARE;
      const color = on ? COMPARE_COLORS[compare.filters.indexOf(filter) % COMPARE_COLORS.length] : undefined;
      return (
        <label key={key} className={`cx-opt${on ? ' is-on' : ''}${disabled ? ' is-disabled' : ''}`}>
          <input type="checkbox" data-cx-item checked={on} disabled={disabled} onChange={() => toggleCompare(filter)} />
          {color ? <span className="cx-dot" style={{ background: color }} aria-hidden /> : null}
          <span className="cx-opt-main">
            <span className="cx-opt-name">{label}</span>
            <span className="cx-opt-sub">{sub}</span>
          </span>
        </label>
      );
    }
    const { members, picked } = groupState(filter);
    const total = members.length || picked;
    const on = total > 0 && picked === total;
    const some = picked > 0 && !on;
    return (
      <label
        key={key}
        className={`cx-opt${on ? ' is-on' : ''}${some ? ' is-some' : ''}`}
        data-scope-org={filter === ORG_FILTER ? '' : undefined}
      >
        <input
          type="checkbox"
          data-cx-item
          checked={on}
          ref={el => {
            if (el) el.indeterminate = some;
          }}
          onChange={() => toggleGroup(filter)}
        />
        <span className="cx-opt-main">
          <span className="cx-opt-name">{label}</span>
          <span className="cx-opt-sub">
            {some ? t('context.panel.venues.groupPartial', { count: picked, total }) : sub}
          </span>
        </span>
      </label>
    );
  };

  const groupIdOfDraft = scopeGroupIdFromFilter(draft);

  return (
    <div className="cx-venues">
      {compare.supported ? (
        <label className="cx-switch">
          <input
            type="checkbox"
            checked={compare.on}
            onChange={e =>
              onCompare({
                ...compare,
                on: e.target.checked,
                filters: e.target.checked
                  ? compare.filters.length
                    ? compare.filters
                    : draft && draft !== 'all'
                      ? [draft]
                      : []
                  : compare.filters,
              })
            }
          />
          <span className="cx-switch-track" aria-hidden />
          <span className="cx-switch-label">
            {t('context.panel.venues.compare')}
            <small>
              {compare.on
                ? t('context.panel.venues.compareCount', { count: compare.filters.length, max: MAX_COMPARE })
                : t('context.panel.venues.compareHint')}
            </small>
          </span>
        </label>
      ) : compare.on && compare.filters.length >= 2 ? (
        <p className="cx-note is-muted" role="note">
          {t('context.panel.venues.compareNotHere', { screen: screenName })}{' '}
          <button
            type="button"
            className="cx-link"
            onClick={() => onCompare({ ...compare, on: false, filters: [] })}
          >
            {t('context.panel.venues.stopCompare')}
          </button>
        </p>
      ) : null}

      <div className="cx-search">
        <Icon name="search" size={14} />
        <input
          type="search"
          aria-label={t('context.panel.venues.search')}
          placeholder={t('context.panel.venues.searchPlaceholder')}
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {!compare.on && ids.length ? (
        <div className="cx-picked" role="group" aria-labelledby="cx-picked-head">
          <div className="cx-group-head">
            <span id="cx-picked-head">{t('context.panel.venues.selected', { count: ids.length })}</span>
            <button type="button" className="cx-link" data-cx-clear onClick={() => setIds([])}>
              {t('context.panel.venues.clear')}
            </button>
          </div>
          <ul className="cx-tokens">
            {shownTokens.map(tk => (
              <li key={tk.key} className={`cx-token${tk.total > 1 ? ' is-group' : ''}`}>
                <span className="cx-token-name">{tk.label}</span>
                {tk.total > 1 ? (
                  <span className="cx-token-count">{tk.picked < tk.total ? `${tk.picked}/${tk.total}` : tk.total}</span>
                ) : null}
                <button
                  type="button"
                  className="cx-token-x"
                  aria-label={t('context.panel.venues.remove', { name: tk.label })}
                  title={t('context.panel.venues.remove', { name: tk.label })}
                  onClick={() => removeToken(tk.remove)}
                >
                  <Icon name="close" size={11} />
                </button>
              </li>
            ))}
            {moreTokens > 0 ? (
              <li>
                <button type="button" className="cx-link" onClick={() => setSelectedOnly(true)}>
                  {t('context.panel.venues.more', { count: moreTokens })}
                </button>
              </li>
            ) : null}
          </ul>
        </div>
      ) : null}
      {limitNote ? (
        <p className="cx-note is-error" role="alert">
          {t('context.panel.venues.limit', { max: VENUES_FILTER_MAX })}
        </p>
      ) : null}

      <fieldset className="cx-group" onKeyDown={onListKeyDown}>
        <legend className="cx-group-head">
          <span>{t('context.panel.venues.groups')}</span>
          {onManageGroups ? (
            <button type="button" className="cx-link" onClick={onManageGroups}>
              <Icon name="sliders" size={12} />
              {t('context.panel.venues.manage')}
            </button>
          ) : null}
        </legend>
        {showOrg
          ? groupRow(
              ORG_FILTER,
              orgFilterLabel(orgVenues?.name ?? null),
              t('scope.allVenuesSub', { count: orgIds.length }),
              'org',
            )
          : null}
        {groups.map(g =>
          groupRow(scopeGroupFilterKey(g.id), g.name, t('context.venues.count', { count: g.count }), g.id),
        )}
        {!groups.length ? <p className="cx-empty">{t('context.panel.venues.noGroups')}</p> : null}
      </fieldset>

      <fieldset className="cx-group" onKeyDown={onListKeyDown}>
        <legend className="cx-group-head">
          <span>{t('context.panel.venues.list')}</span>
          {!compare.on ? (
            <span className="cx-group-actions">
              {rows.length > 1 ? (
                <button
                  type="button"
                  className="cx-link"
                  onClick={toggleAll}
                  disabled={!allMatchesChecked && selectAllSize > VENUES_FILTER_MAX}
                  title={
                    !allMatchesChecked && selectAllSize > VENUES_FILTER_MAX
                      ? t('context.panel.venues.limit', { max: VENUES_FILTER_MAX })
                      : undefined
                  }
                >
                  {allMatchesChecked
                    ? t('context.panel.venues.clearAll')
                    : search.trim()
                      ? t('context.panel.venues.selectMatches', { count: rows.length })
                      : t('context.panel.venues.selectAll', { count: rows.length })}
                </button>
              ) : null}
              <label className="cx-inline-check">
                <input type="checkbox" checked={selectedOnly} onChange={e => setSelectedOnly(e.target.checked)} />
                {t('context.panel.venues.selectedOnly')}
              </label>
            </span>
          ) : null}
        </legend>
        <div className="cx-list" role="group">
          {visible.map(r => {
            const filter = outletFilterKey(r.id);
            const on = compare.on ? compareSet.has(filter) : checked.has(r.id);
            const disabled = compare.on && !on && compare.filters.length >= MAX_COMPARE;
            const color =
              compare.on && on ? COMPARE_COLORS[compare.filters.indexOf(filter) % COMPARE_COLORS.length] : undefined;
            return (
              <label key={r.id} className={`cx-opt${on ? ' is-on' : ''}${disabled ? ' is-disabled' : ''}`}>
                <input type="checkbox" data-cx-item checked={on} disabled={disabled} onChange={() => toggle(r.id)} />
                {color ? <span className="cx-dot" style={{ background: color }} aria-hidden /> : null}
                <span className="cx-opt-main">
                  <span className="cx-opt-name">{r.name}</span>
                  <span className="cx-opt-sub">{[r.city, r.region].filter(Boolean).join(' · ')}</span>
                </span>
                {r.id === activeRestaurantId ? <span className="cx-tag">{t('context.panel.venues.opened')}</span> : null}
              </label>
            );
          })}
          {!rows.length ? (
            <p className="cx-empty">
              {selectedOnly ? t('context.panel.venues.noneSelected') : t('context.panel.venues.none')}
            </p>
          ) : null}
          {rows.length > RESULT_CAP ? (
            <p className="cx-empty">
              {t('context.panel.venues.showingFirst', { cap: RESULT_CAP, total: rows.length.toLocaleString() })}
            </p>
          ) : null}
        </div>
      </fieldset>

      {!compare.on ? (
        <div className="cx-venue-foot">
          <span className="cx-venue-count" aria-live="polite">
            {ids.length
              ? groupIdOfDraft
                ? `${groups.find(g => g.id === groupIdOfDraft)?.name ?? ''} · ${t('context.panel.venues.nSelected', { count: ids.length })}`
                : isOrgFilter(draft)
                  ? `${orgFilterLabel(orgVenues?.name ?? null)} · ${t('context.panel.venues.nSelected', { count: ids.length })}`
                  : t('context.panel.venues.nSelected', { count: ids.length })
              : t('context.panel.venues.pickOne')}
          </span>
          {saveOpen ? (
            <form
              className="cx-save"
              onSubmit={e => {
                e.preventDefault();
                void saveGroup();
              }}
            >
              <input
                type="text"
                className="cx-save-input"
                aria-label={t('context.panel.venues.groupName')}
                placeholder={t('context.panel.venues.groupName')}
                value={saveName}
                onChange={e => setSaveName(e.target.value)}
                autoFocus
              />
              <button type="submit" className="cx-btn" disabled={saving || !saveName.trim() || !ids.length}>
                {saving ? t('context.panel.venues.saving') : t('context.panel.venues.create')}
              </button>
            </form>
          ) : (
            <button
              type="button"
              className="cx-link"
              disabled={!ids.length}
              onClick={() => {
                setSaveOpen(true);
                setSaveNote(null);
              }}
            >
              {t('context.panel.venues.saveAs')}
            </button>
          )}
          {saveNote ? (
            <p className={`cx-note${saveNote.ok ? '' : ' is-error'}`} role={saveNote.ok ? 'status' : 'alert'}>
              {saveNote.text}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
